import { generateHTML } from "@tiptap/html";
import * as marked from "marked";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as ExclamationTriangleIcon } from "../../Assets/exclamation-triangle.svg";
import { ReactComponent as ChevronRightIcon } from "../../Assets/angle-right-solid.svg";
import { ReactComponent as BugIcon } from "../../Assets/bug-solid.svg";
import { ReactComponent as CaretUpIcon } from "../../Assets/caret-up.svg";
import { ReactComponent as CheckIcon } from "../../Assets/check-solid.svg";
import { ReactComponent as PaperclipIcon } from "../../Assets/paperclip-solid.svg";
import { ReactComponent as PlusIcon } from "../../Assets/plus-solid.svg";
import { ReactComponent as ThumbsDown } from "../../Assets/thumbs-down-regular.svg";
import { ReactComponent as ThumbsUp } from "../../Assets/thumbs-up-regular.svg";
import { ReactComponent as ThumbsDownActive } from "../../Assets/thumbs-down-solid.svg";
import { ReactComponent as ThumbsUpActive } from "../../Assets/thumbs-up-solid.svg";
import Communicator from "../../Helper/Communicator";
import { contentWithVariables } from "../../Helper/ContentReplace";
import { getDataDescription } from "../../Helper/FeedbackItemHelper";
import { isOnline } from "../../Helper/OnlineState";
import { replaceAll } from "../../Helper/String";
import { useAppViewStore } from "../../Stores/AppView";
import { useConfigStore } from "../../Stores/Config";
import { useFeatureRequestStore } from "../../Stores/FeatureRequests";
import { useFormStore } from "../../Stores/Form";
import { useRouterStore } from "../../Stores/Router";
import { useSessionStore } from "../../Stores/Session";
import { Ticket, TicketChatMessage, useTicketStore } from "../../Stores/Ticket";
import ActionButton from "../AIActionButton/AIActionButton";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import BotActionChatMessage from "../BotActionChatMessage/BotActionChatMessage";
import ChatAvatar from "../ChatAvatar/ChatAvatar";
import FeatureRequestSuggestions from "../FeatureRequestSuggestions/FeatureRequestSuggestions";
import FormLoading from "../FormLoading/FormLoading";
import LoadingText from "../LoadingText/LoadingText";
import { ratingOptions } from "../RatingFormItem/RatingFormItem";
import "./ChatMessage.scss";
import { handleGleapLink } from "../..";
import ExtensionKit from "../tiptap/extenstions/extension-kit";

const renderer = new marked.Renderer();
renderer.link = function ({ href, title, tokens }) {
  const text = tokens.map((token) => token.raw).join("");
  return `<a target="_blank" href="${href}">${text}</a>`;
};

marked.setOptions({
  renderer: renderer,
});

function ChatMessage({
  chatMessage,
  lastOfGroup = false,
  isCreator = false,
  showTime = true,
  typingDelay = 0,
  forceTyping = false,
  imageLoaded = undefined,
}: {
  chatMessage: TicketChatMessage;
  lastOfGroup?: boolean;
  isCreator?: boolean;
  showTime?: boolean;
  typingDelay?: number;
  forceTyping?: boolean;
  imageLoaded?: () => void;
}) {
  const messageTextRef = useRef<HTMLDivElement>(null);
  const configStore = useConfigStore();
  const feedbackItemStore = useTicketStore();
  const currentAction = feedbackItemStore.currentBotAction?.action;
  const [isTyping, setIsTyping] = useState(typingDelay > 0 ? true : false);

  useEffect(() => {
    const messageElement = messageTextRef.current;
    const handleImageClick = (e: MouseEvent) => {
      if (e.target instanceof HTMLImageElement) {
        Communicator.openImage(e.target.src);
      }
    };

    messageElement?.addEventListener("click", handleImageClick);
    return () => messageElement?.removeEventListener("click", handleImageClick);
  }, []);

  useEffect(() => {
    var timer: any = null;
    if (typingDelay && typingDelay > 0) {
      timer = setTimeout(() => {
        setIsTyping(false);
      }, typingDelay);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  const openingTimeAutoResponderText = () => {
    const { online, nextOpeningTime, currentOpeningTime } = isOnline();

    var message = "";
    if (online) {
      let currentReplyTime =
        currentOpeningTime?.replyTime ?? configStore.config.replyTime;

      message = `${configStore.translateText(currentReplyTime)}.`;
    } else {
      const oooMessage = configStore.translateText("outOfOfficeMessage");
      if (oooMessage.trim() !== "" && oooMessage !== "outOfOfficeMessage") {
        message = configStore.translateText("outOfOfficeMessage");
      } else {
        message = `${configStore.translateText(
          "weAreOffline"
        )}\n\n${configStore.translateText("weAreBack")} <b>🕘 ${moment(
          nextOpeningTime
        ).fromNow()}</b>.`;
      }
    }

    return message;
  };

  const renderContent = () => {
    if (isTyping || forceTyping) {
      return <div className="dot-flashing" />;
    }

    if (chatMessage.type === "FEATURE_REQUEST_DUPLICATES") {
      return <FeatureRequestSuggestions />;
    }

    if (chatMessage?.data?.answerBot && !chatMessage.data?.hasAnswer) {
      return (
        <div className="message-text">
          {configStore.translateText("kaiIssue")}
        </div>
      );
    }

    if (
      chatMessage.type === "TEXT" ||
      chatMessage.type === "USER_TEXT" ||
      chatMessage.type === "SHARED_COMMENT" ||
      chatMessage.type === "EMAIL_MESSAGE" ||
      chatMessage.type === "BOT"
    ) {
      // Special message for reply times.
      if (chatMessage.data?.showReplyTimes) {
        var openingTime = openingTimeAutoResponderText();
        if (!openingTime) {
          return null;
        }

        return (
          <div
            className="message-text"
            dangerouslySetInnerHTML={{ __html: openingTime }}
          ></div>
        );
      }

      // Special message for scheduled events.
      if (chatMessage.data?.scheduledMeetingData) {
        const { scheduledMeetingData } = chatMessage.data;

        return (
          <div className="message-text">
            <h3 className="scheduled-title">
              {configStore.translateText("scheduled")}:{" "}
              {scheduledMeetingData.name}
            </h3>
            <div className="scheduled-date">
              {new Date(scheduledMeetingData?.startTime).toLocaleDateString(
                navigator.language,
                {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }
              )}
            </div>
            {scheduledMeetingData?.user && (
              <div className="scheduled-user">
                {scheduledMeetingData?.user?.name} (
                <a href={`mailto:${scheduledMeetingData?.user?.email}`}>
                  {scheduledMeetingData?.user?.email}
                </a>
                )
              </div>
            )}
          </div>
        );
      }

      var contentData = chatMessage?.data?.content;

      var content: any = "";
      if (contentData) {
        if (
          contentData &&
          (typeof contentData === "string" || contentData instanceof String)
        ) {
          // If it's a plain text message, we need to enhance it with code blocks.
          content = contentData;

          content = replaceAll(content, "\n", "<br>");
        } else {
          if (chatMessage.data.content) {
            try {
              content = generateHTML(chatMessage.data.content, [
                ...ExtensionKit({}),
              ]);

              let regex = /<helpcenterarticle.*?>.*?<\/helpcenterarticle>/gs;
              let matches = Array.from(content.matchAll(regex));

              for (let i = 0; i < matches.length; i++) {
                let match: any = matches[i];
                let textToReplace = match[0];

                // perform the necessary replacements on textToReplace
                let replacedText = replaceAll(textToReplace, "&lt;", "<");
                replacedText = replaceAll(replacedText, "&gt;", ">");
                replacedText = replaceAll(replacedText, "&quot;", '"');
                replacedText = replaceAll(
                  replacedText,
                  "helpcenterarticle",
                  "div"
                );
                // replace the original text with the modified text
                content = content.replace(textToReplace, replacedText);
              }

              content = replaceAll(content, "\n", "<br>");
            } catch (err) {
              console.error(err);
            }
          }
        }
      } else if (
        chatMessage?.data?.text &&
        (typeof chatMessage.data.text === "string" ||
          chatMessage.data.text instanceof String)
      ) {
        // Replace new lines.
        content = replaceAll(chatMessage.data.text, "\n", "<br>");
      }

      content = contentWithVariables(content, {
        name: useSessionStore.getState().getName(),
      });

      try {
        // Replace <br> with newlines and ensure Markdown headers are correctly formatted
        content = content.replace(/<br\s*\/?>(?=\s*|$)/gi, "\n"); // Replace <br>, <br/>, <br /> with newlines
        content = content.replace(/^\s*(#{1,6})/gm, "$1"); // Ensure headers are properly formatted
        content = content.trim(); // Trim leading/trailing whitespace
        content = content.replace(/\n{2,}/g, "\n\n"); // Ensure single blank lines between blocks

        // Replace annotations.
        if (configStore.config.showSources ?? true) {
          content = content.replace(
            /\[\^(\d+)\|([^|]+)\|([^\]]+)\]/g,
            `<a href="$3" target="_blank" class="ai-annotation" data-title="$2">$1</a>`
          );
        } else {
          content = content.replace(/\[\^(\d+)\|([^|]+)\|([^\]]+)\]/g, "");
        }

        content = marked.parse(content, {
          async: false,
          breaks: true,
        });
      } catch (err) {
        console.error(err);
      }

      return (
        <div
          ref={messageTextRef}
          className="message-text"
          dangerouslySetInnerHTML={{ __html: content }}
        ></div>
      );
    }
    if (chatMessage.type === "BOT_REPLY") {
      let message = chatMessage?.data?.content;

      if (Array.isArray(message)) {
        message = message.join(", ");
      } else if (typeof message === "object") {
        message = JSON.stringify(message);
      }

      return <div className="message-text">{message}</div>;
    }
    if (chatMessage.type === "FEEDBACK_ITEM") {
      const feedbackItem = chatMessage.data as Ticket;

      if (feedbackItem?.title) {
        const formKeys = feedbackItem.form
          ? Object.keys(feedbackItem.form)
          : [];

        if (formKeys.length === 0) {
          return null;
        }

        return (
          <div>
            {feedbackItem.type === "FEATURE_REQUEST" && (
              <strong>{feedbackItem.title}</strong>
            )}
            {formKeys?.length > 0 &&
              formKeys.map((formKey) => {
                const formItem = feedbackItem.form[formKey];

                let value = formItem.value;

                if (formItem.type === "multiplechoice") {
                  value =
                    formItem?.choices?.find(
                      (choice: any) => choice?.id === formItem.value
                    )?.label ?? "--";
                }

                if (
                  formItem.type === "multiple-choice-multi-select" &&
                  Array.isArray(formItem.value)
                ) {
                  const labels = formItem.value
                    .map((val: string) => {
                      const choice = formItem?.choices?.find(
                        (choice: any) => choice && choice?.id === val
                      );
                      return choice ? choice.label : null;
                    })
                    .filter(Boolean);

                  value = labels.join(", ") || "--";
                }

                return (
                  <div key={formKey} className="message-form-item">
                    <div className="message-form-item-value">{value}</div>
                  </div>
                );
              })}
          </div>
        );
      }

      const content = getDataDescription({
        data: feedbackItem,
        maxLength: 10000,
      });
      if (content === "No content") {
        return null;
      }

      return <div className="message-text">{content}</div>;
    }

    return null;
  };

  const renderAdditionalContent = () => {
    if (chatMessage.type === "FEEDBACK_ITEM") {
      const feedbackItem = chatMessage.data as Ticket;

      if (feedbackItem && feedbackItem?.screenRecordingUrl) {
        return (
          <div className="chat-message-image">
            <video muted controls src={feedbackItem?.screenRecordingUrl} />
          </div>
        );
      }

      if (feedbackItem && feedbackItem.screenshotUrl) {
        return (
          <div className="chat-message-image">
            <img
              src={feedbackItem.screenshotUrl}
              onLoad={() => {
                if (imageLoaded) {
                  imageLoaded();
                }
              }}
              onClick={() => {
                Communicator.openImage(feedbackItem.screenshotUrl || "");
              }}
            />
          </div>
        );
      } else {
        if (feedbackItem.generatingScreenshot) {
          return (
            <div className="chat-message-loading">
              <FormLoading />
            </div>
          );
        }
      }

      return null;
    }

    return null;
  };

  const isFileImage = (file: any) => {
    return file && file["type"] && file["type"].split("/")[0] === "image";
  };

  if (chatMessage.type === "BOT_INPUT_REPLY") {
    return (
      <BotActionChatMessage
        date={chatMessage.createdAt}
        hideAvatar={!lastOfGroup}
      >
        <div className="bot-input-reply">
          {chatMessage?.data?.type === "rateconversation" ? (
            <>
              <div className="bot-input-reply-label">
                {chatMessage?.data?.question}
              </div>
              <div className="bot-input-reply-rating-response">
                {[1, 2.5, 5, 7.5, 10].map((value: any, index: number) => {
                  return (
                    <div
                      key={index}
                      className={`bot-input-reply-rating-response-option ${
                        chatMessage?.data?.rating === value &&
                        "bot-input-reply-rating-response-option--selected"
                      }`}
                    >
                      {ratingOptions[index]}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="bot-input-reply-label">
                {chatMessage?.data?.question}
              </div>
              <div className="bot-input-reply-response">
                {chatMessage?.data?.content}
              </div>
              <CheckIcon className="bot-input-reply-check" />
            </>
          )}
        </div>
      </BotActionChatMessage>
    );
  }

  const content = renderContent();
  if (!content) {
    return null;
  }

  const messageFromKai = !(
    chatMessage?.data?.profileImageUrl ||
    configStore.config.operatorAvatarImage?.length
  );

  const renderCustomActions = () => {
    if (
      !chatMessage.data?.buttonActions ||
      chatMessage.data?.buttonActions.length === 0
    ) {
      return null;
    }

    return (
      <div className="ai-action-buttons">
        {chatMessage.data?.buttonActions.map((action: any, index: number) => {
          return <ActionButton key={index} action={action} index={index} />;
        })}
      </div>
    );
  };

  const customActions = renderCustomActions();

  const renderAudioAttachments = () => {
    if (!chatMessage?.data?.audioFile) {
      return null;
    }

    return (
      <div className="ai-audio-attachment">
        <AudioPlayer audioSrc={chatMessage.data.audioFile} autoPlay={true} />
      </div>
    );
  };

  return (
    <div
      className={`chat-message ${
        chatMessage.type === "FEATURE_REQUEST_DUPLICATES" &&
        "chat-message--featurerequest-duplicates"
      } ${customActions && "chat-message-ai-action"} ${
        forceTyping && "chat-message--typing"
      } ${
        (chatMessage.data?.answerBot || messageFromKai) && "chat-message--aibot"
      } ${isCreator && "chat-message--creator"} ${
        lastOfGroup && "chat-message--last-of-group"
      } ${chatMessage?.data?.isWritting && "chat-message--writting"}`}
    >
      <ChatAvatar user={chatMessage.user} session={chatMessage.session} />
      <div className="message-container">
        <div className="message">
          {chatMessage?.data?.isWritting ? (
            <LoadingText
              text={chatMessage?.data?.content ?? "I'm working on it..."}
            ></LoadingText>
          ) : (
            <>
              {content}
              {customActions}
              {!customActions && (() => {
                const sources = chatMessage.data?.sources ?? [];
                const filteredSources = sources
                  .slice(0, 3)
                  .filter((source: any) => {
                    // Always show createfeaturerequest and reportabug types
                    if (source.type === "createfeaturerequest" || source.type === "reportabug") {
                      return true;
                    }
                    
                    if (source.type === "featurerequest") {
                      return true;
                    }

                    // Don't show if sources are disabled
                    if (!(configStore.config.showSources ?? true)) {
                      return false;
                    }
                    
                    // Don't show annotated sources
                    if (source.annotated) {
                      return false;
                    }
                    
                    // Don't show sources without url or title
                    if (!source.url || source.url.trim() === "" || !source.title || source.title.trim() === "") {
                      return false;
                    }
                    
                    return true;
                  });

                if (filteredSources.length === 0) {
                  return null;
                }

                return (
                  <div className="link-sources">
                    <div className={`link-sources-links`}>
                      {filteredSources.map((source: any, index: number) => {
                        const onClick = () => {
                          if (source.type === "featurerequest") {
                            useFeatureRequestStore
                              .getState()
                              .openFeatureRequest(source.shareToken, true);
                          } else if (
                            source.type === "createfeaturerequest"
                          ) {
                            const flow = "featurerequests";
                            useFormStore.setState({
                              feedbackFlow: flow,
                              isSurveyFlow: false,
                              preFillData: {
                                description:
                                  source?.data?.description ?? "",
                                title: source?.data?.title ?? "",
                              },
                            });
                            useRouterStore.getState().pushPage("flow", {
                              flowId: flow,
                            });
                            useAppViewStore.getState().reset();
                          } else if (source.type === "reportabug") {
                            const flow = "bugreporting";
                            useFormStore.setState({
                              feedbackFlow: flow,
                              isSurveyFlow: false,
                              preFillData: {
                                description:
                                  source?.data?.description ?? "",
                              },
                            });
                            useRouterStore.getState().pushPage("flow", {
                              flowId: flow,
                            });
                            useAppViewStore.getState().reset();
                          } else {
                            // Check for gleap links.
                            handleGleapLink(source.url, true);
                          }
                        };

                        if (source.type === "createfeaturerequest") {
                          return (
                            <div
                              key={index}
                              className="link-source link-source-button"
                              onClick={onClick}
                            >
                              <div className="link-source--submiticon">
                                <PlusIcon />
                              </div>
                              <div className="link-source--title">
                                {source.title}
                              </div>
                              <ChevronRightIcon className="link-source--arrow" />
                            </div>
                          );
                        }

                        if (source.type === "reportabug") {
                          return (
                            <div
                              key={index}
                              className="link-source link-source-button"
                              onClick={onClick}
                            >
                              <div className="link-source--submiticon">
                                <BugIcon />
                              </div>
                              <div className="link-source--title">
                                {source.title}
                              </div>
                              <ChevronRightIcon className="link-source--arrow" />
                            </div>
                          );
                        }

                        return (
                          <div
                            key={index}
                            className={`link-source ${
                              (source.type === "featurerequest" ||
                                source.type === "createfeaturerequest") &&
                              "link-source-button"
                            }`}
                            onClick={onClick}
                          >
                            {source.type === "featurerequest" && (
                              <div className="link-source--upvotes">
                                <CaretUpIcon />
                                {source.upvotes > 99
                                  ? "99+"
                                  : source.upvotes}
                              </div>
                            )}
                            {source.type === "createfeaturerequest" && (
                              <div className="link-source--submiticon">
                                <PlusIcon />
                              </div>
                            )}
                            <div className="link-source--title">
                              {source.title}
                            </div>
                            <ChevronRightIcon className="link-source--arrow" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              {chatMessage.attachments &&
                chatMessage.attachments.map((attachment, index) => {
                  if (isFileImage(attachment)) {
                    return (
                      <img
                        className="attachment-image"
                        src={attachment.url}
                        alt={attachment.name}
                        onLoad={() => {
                          if (imageLoaded) {
                            imageLoaded();
                          }
                        }}
                        onClick={() => {
                          if (attachment.url) {
                            if (configStore.isApp) {
                              Communicator.openURL(attachment.url);
                            } else {
                              Communicator.openImage(attachment.url);
                            }
                          }
                        }}
                      />
                    );
                  }

                  // Check if the attachment is an audio file
                  if (attachment?.type?.startsWith("audio/")) {
                    return (
                      <AudioPlayer key={index} audioSrc={attachment.url} />
                    );
                  }

                  return (
                    <div
                      className="attachment"
                      key={index}
                      onClick={() => {
                        if (configStore.isApp) {
                          Communicator.openURL(attachment.url);
                        } else {
                          const link = document.createElement("a");
                          link.href = attachment.url;
                          link.setAttribute("download", attachment.name);
                          link.setAttribute("target", "_blank");
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        }
                      }}
                    >
                      <a className="attachment-link">
                        <PaperclipIcon />
                        <span>{attachment.name}</span>
                      </a>
                    </div>
                  );
                })}
              {renderAudioAttachments()}
            </>
          )}
          {chatMessage?.data?.answerBot && (
            <>
              <div className="answer-bot">
                <div>
                  <div className="answer-bot-tag">AI</div>{" "}
                  {configStore.translateText("kaiAnswerTag")}
                </div>
                {currentAction?.type === "answerbotflow" &&
                  (currentAction?.allowRating ?? true) &&
                  !chatMessage?.data?.isWritting && (
                    <div className="answer-bot-feedback">
                      {chatMessage.rating === "negative" ? (
                        <ThumbsDownActive className="answer-bot-feedback-action answer-bot-feedback-action--active" />
                      ) : (
                        <ThumbsDown
                          className="answer-bot-feedback-action"
                          onClick={() => {
                            feedbackItemStore?.rateComment(
                              chatMessage.id!,
                              "negative"
                            );
                          }}
                        />
                      )}

                      {chatMessage.rating === "positive" ? (
                        <ThumbsUpActive className="answer-bot-feedback-action answer-bot-feedback-action--active" />
                      ) : (
                        <ThumbsUp
                          className="answer-bot-feedback-action"
                          onClick={() => {
                            feedbackItemStore?.rateComment(
                              chatMessage.id!,
                              "positive"
                            );
                          }}
                        />
                      )}
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
        {renderAdditionalContent()}
        {showTime && (
          <div className="message-date">
            {(chatMessage.type === "BOT" || chatMessage.data?.answerBot) &&
              `${configStore.getBotName()} · `}
            {chatMessage.sending
              ? `Sending...`
              : chatMessage.error
              ? ""
              : moment(chatMessage.createdAt ?? new Date()).fromNow()}
            {chatMessage.error && (
              <div className="message-date-error">
                <ExclamationTriangleIcon />
                Network error. Please try again later.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatMessage;
